import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { planningApi } from '../api/planning.api'
import type { PlanningCompleteDto } from '@/shared/types/planning'

const QUERY_KEY = ['planning']

export function usePlanning(etudiantId: number | undefined) {
  return useQuery({
    queryKey: [...QUERY_KEY, etudiantId],
    queryFn: () => planningApi.getByEtudiantId(etudiantId!),
    enabled: !!etudiantId,
  })
}

// Helper: walk the planning cache and toggle `actif` on a specific ProgrammePlanning chip
function toggleActifInCache(
  old: PlanningCompleteDto,
  targetId: number,
  actif: boolean,
): PlanningCompleteDto {
  return {
    ...old,
    plannings: old.plannings.map((p) => {
      const newCells: typeof p.cells = {}
      for (const rowId of Object.keys(p.cells)) {
        newCells[rowId] = {}
        for (const day of Object.keys(p.cells[rowId])) {
          const cell = p.cells[rowId][day]
          newCells[rowId][day] = {
            ...cell,
            programmePlannings: cell.programmePlannings.map((pp) =>
              pp.id === targetId ? { ...pp, actif } : pp,
            ),
          }
        }
      }
      return { ...p, cells: newCells }
    }),
  }
}

export function useUpdateProgrammePlanning(etudiantId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, jourDeclenchement }: { id: number; jourDeclenchement: number }) =>
      planningApi.updateProgrammePlanning(id, jourDeclenchement),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...QUERY_KEY, etudiantId] }),
  })
}

export function useDeactivateProgrammePlanning(etudiantId: number) {
  const qc = useQueryClient()
  const queryKey = [...QUERY_KEY, etudiantId]

  return useMutation({
    mutationFn: (id: number) => planningApi.deactivateProgrammePlanning(id),
    onMutate: async (id) => {
      // Cancel any ongoing refetch so it doesn't overwrite our optimistic update
      await qc.cancelQueries({ queryKey })
      const previous = qc.getQueryData<PlanningCompleteDto>(queryKey)
      if (previous) {
        qc.setQueryData(queryKey, toggleActifInCache(previous, id, false))
      }
      return { previous }
    },
    onError: (_err, _id, context) => {
      // Rollback on error
      if (context?.previous) {
        qc.setQueryData(queryKey, context.previous)
      }
    },
    // Refetch to sync with backend (which now includes deactivated items)
    onSettled: () => qc.invalidateQueries({ queryKey }),
  })
}

export function useReactivateProgrammePlanning(etudiantId: number) {
  const qc = useQueryClient()
  const queryKey = [...QUERY_KEY, etudiantId]

  return useMutation({
    mutationFn: (id: number) => planningApi.reactivateProgrammePlanning(id),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey })
      const previous = qc.getQueryData<PlanningCompleteDto>(queryKey)
      if (previous) {
        qc.setQueryData(queryKey, toggleActifInCache(previous, id, true))
      }
      return { previous }
    },
    onError: (_err, _id, context) => {
      if (context?.previous) {
        qc.setQueryData(queryKey, context.previous)
      }
    },
    // Reactivation: refetch to get the full server state back
    onSettled: () => qc.invalidateQueries({ queryKey }),
  })
}

export function useUpdateLangueStart(etudiantId: number) {
  const qc = useQueryClient()
  const queryKey = [...QUERY_KEY, etudiantId]

  return useMutation({
    mutationFn: ({ etudiantMatiereId, debutLundi }: { etudiantMatiereId: number; debutLundi: boolean }) =>
      planningApi.updateLangueStart(etudiantMatiereId, debutLundi),
    onMutate: async ({ etudiantMatiereId, debutLundi }) => {
      await qc.cancelQueries({ queryKey })
      const previous = qc.getQueryData<PlanningCompleteDto>(queryKey)
      if (previous) {
        qc.setQueryData<PlanningCompleteDto>(queryKey, {
          ...previous,
          plannings: previous.plannings.map((p) => ({
            ...p,
            langues: p.langues.map((l) =>
              l.etudiantMatiereId === etudiantMatiereId ? { ...l, debutLundi } : l,
            ),
          })),
        })
      }
      return { previous }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        qc.setQueryData(queryKey, context.previous)
      }
    },
    onSettled: () => qc.invalidateQueries({ queryKey }),
  })
}
